import React, { useState, useEffect } from 'react';
import { Package, Eye, Search, RefreshCw, ArrowRight } from 'lucide-react';
import { dataInspectionService } from '../../../../../services/dataInspectionService';
import { StcTransOrderDto, StcTransOrderLineDto } from '../../../../../types/dataInspection';
import { BackButton } from '../../../../common/BackButton';

interface StcTransOrdersViewerProps {
    onBack: () => void;
}

export const StcTransOrdersViewer: React.FC<StcTransOrdersViewerProps> = ({ onBack }) => {
    const [stcTransOrders, setStcTransOrders] = useState<StcTransOrderDto[]>([]);
    const [selectedStcTransOrder, setSelectedStcTransOrder] = useState<StcTransOrderDto | null>(null);
    const [stcTransOrderLines, setStcTransOrderLines] = useState<StcTransOrderLineDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>('');
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        loadStcTransOrders();
    }, []);

    const loadStcTransOrders = async () => {
        try {
            setLoading(true);
            setError('');
            const data = await dataInspectionService.getAllStcTransOrders();
            setStcTransOrders(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'En feil oppstod');
        } finally {
            setLoading(false);
        }
    };

    const loadStcTransOrderLines = async (orderNo: number) => {
        try {
            setLoading(true);
            setError('');
            const data = await dataInspectionService.getStcTransOrderWithLines(orderNo);
            setSelectedStcTransOrder(data.header);
            setStcTransOrderLines(data.lines);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'En feil oppstod');
        } finally {
            setLoading(false);
        }
    };

    const handleViewStcTransOrder = (stcTransOrder: StcTransOrderDto) => {
        loadStcTransOrderLines(stcTransOrder.orderNo);
    };

    const handleBackToList = () => {
        setSelectedStcTransOrder(null);
        setStcTransOrderLines([]);
    };

    const filteredStcTransOrders = stcTransOrders.filter(order => 
        order.orderNo.toString().includes(searchTerm) ||
        order.orderNoERP?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.operatorID?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const getStatusColor = (status: number) => {
        switch (status) {
            case 0: return 'bg-gray-100 text-gray-800';
            case 1: return 'bg-blue-100 text-blue-800';
            case 2: return 'bg-yellow-100 text-yellow-800';
            case 3: return 'bg-green-100 text-green-800';
            case 4: return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusText = (status: number) => {
        switch (status) {
            case 0: return 'Ny';
            case 1: return 'Planlagt';
            case 2: return 'Under utførelse';
            case 3: return 'Ferdig';
            case 4: return 'Kansellert';
            default: return `Status ${status}`;
        }
    };

    if (selectedStcTransOrder) {
        return (
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center gap-3 mb-6">
                    <BackButton
                        onClick={handleBackToList}
                        className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
                        iconSize={16}
                        label="Tilbake til liste"
                    />
                    <div className="flex items-center gap-2">
                        <Package className="w-5 h-5 text-orange-600" />
                        <h1 className="text-xl font-semibold">Lageroverføring {selectedStcTransOrder.orderNo}</h1>
                    </div>
                </div>

                {/* StcTransOrder Header Info */}
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div>
                            <label className="text-sm font-medium text-gray-500">Ordre Nr.</label>
                            <div className="text-lg font-semibold text-gray-900">{selectedStcTransOrder.orderNo}</div>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-500">ERP Nr.</label>
                            <div className="text-gray-900">{selectedStcTransOrder.orderNoERP || 'N/A'}</div>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-500">Status</label>
                            <div className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedStcTransOrder.orderStatus)}`}>
                                {getStatusText(selectedStcTransOrder.orderStatus)}
                            </div>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-500">Fra lokasjon</label>
                            <div className="text-gray-900">{selectedStcTransOrder.fromLocation || 'N/A'}</div>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-500">Til lokasjon</label>
                            <div className="text-gray-900">{selectedStcTransOrder.toLocation || 'N/A'}</div>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-500">Operatør</label>
                            <div className="text-gray-900">{selectedStcTransOrder.operatorID || 'N/A'}</div>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-500">Ordredato</label>
                            <div className="text-gray-900">
                                {selectedStcTransOrder.orderDate ? new Date(selectedStcTransOrder.orderDate).toLocaleDateString('nb-NO') : 'N/A'}
                            </div>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-500">Leveringsdato</label>
                            <div className="text-gray-900">
                                {selectedStcTransOrder.deliveryDate ? new Date(selectedStcTransOrder.deliveryDate).toLocaleDateString('nb-NO') : 'N/A'}
                            </div>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-500">Beskrivelse</label>
                            <div className="text-gray-900">{selectedStcTransOrder.description || 'N/A'}</div>
                        </div>
                    </div>
                </div>

                {/* StcTransOrder Lines */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                    <div className="p-6 border-b border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-900">Overføringslinjer ({stcTransOrderLines.length})</h2>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Linje</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vare</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Beskrivelse</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mengde</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Behandlet</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fra lokasjon</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Til lokasjon</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {stcTransOrderLines.map(line => (
                                    <tr key={line.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            {line.lineNo}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {line.itemNo}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-900">
                                            {line.description1}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {line.quantity}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {line.quantityProcessed || 0}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {line.fromBinCode || 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {line.toBinCode || 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(line.lineStatus)}`}>
                                                {getStatusText(line.lineStatus)}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
                <BackButton
                    onClick={onBack}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
                    iconSize={16}
                    label="Tilbake til grunndata"
                />
                <div className="flex items-center gap-2">
                    <Package className="w-5 h-5 text-orange-600" />
                    <h1 className="text-xl font-semibold">Lageroverføringer</h1>
                </div>
            </div>

            {/* Search and Controls */}
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                    <div className="flex items-center gap-4 flex-1">
                        <div className="relative flex-1 max-w-md">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <input
                                type="text"
                                placeholder="Søk etter ordre nr., operatør eller beskrivelse..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                    </div>
                    <button
                        onClick={loadStcTransOrders}
                        disabled={loading}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        Oppdater
                    </button>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-red-700">
                        <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                        <span className="font-medium">Feil:</span>
                    </div>
                    <p className="text-red-600 mt-1">{error}</p>
                </div>
            )}

            {/* StcTransOrders Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-6 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">Lageroverføringer ({filteredStcTransOrders.length})</h2>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ordre Nr.</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ERP Nr.</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Operatør</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ordredato</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Beskrivelse</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Handlinger</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredStcTransOrders.map(order => (
                                <tr key={order.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {order.orderNo}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {order.orderNoERP || 'N/A'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.orderStatus)}`}>
                                            {getStatusText(order.orderStatus)}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {order.operatorID || 'N/A'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {order.orderDate ? new Date(order.orderDate).toLocaleDateString('nb-NO') : 'N/A'}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                                        {order.description || 'N/A'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <button
                                            onClick={() => handleViewStcTransOrder(order)}
                                            className="flex items-center gap-1 text-blue-600 hover:text-blue-900 hover:bg-blue-50 px-2 py-1 rounded transition-colors"
                                        >
                                            <Eye className="w-4 h-4" />
                                            Vis detaljer
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};